import React from 'react';

/**
 * Standardized loading spinner component for Super Admin Portal
 * Uses cyan-600 color to match the portal's theme
 */
const LoadingSpinner = ({
  size = 'md',
  message = 'Loading data...',
  className = '',
  showMessage = true,
  fullHeight = false
}) => {
  // Size variants
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
    xl: 'h-16 w-16'
  };

  // Container height variants
  const containerClasses = fullHeight
    ? 'flex items-center justify-center min-h-screen'
    : 'flex items-center justify-center py-12';

  return (
    <div className={`${containerClasses} ${className}`}>
      <div className="text-center">
        <div
          className={`animate-spin rounded-full border-b-2 border-cyan-600 mx-auto ${sizeClasses[size]} ${showMessage ? 'mb-4' : ''}`}
        />
        {showMessage && (
          <p className="text-slate-600">{message}</p>
        )}
      </div>
    </div>
  );
};

/**
 * Inline loading spinner for buttons and small spaces
 */
export const InlineSpinner = ({ size = 'sm', className = '' }) => {
  const sizeClasses = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-5 w-5'
  };

  return (
    <div
      className={`animate-spin rounded-full border-b-2 border-cyan-600 ${sizeClasses[size]} ${className}`}
    />
  );
};

/**
 * Table loading spinner - specifically for table content areas
 */
export const TableLoadingSpinner = ({ message = 'Loading table data...', colSpan = 1 }) => {
  return (
    <tr>
      <td colSpan={colSpan} className="table-cell text-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600 mx-auto mb-4" />
          <p className="text-slate-600">{message}</p>
        </div>
      </td>
    </tr>
  );
};

/**
 * Page loading spinner - for full page loads
 */
export const PageLoadingSpinner = ({ message = 'Loading page content...' }) => {
  return (
    <LoadingSpinner
      size="lg"
      message={message}
      fullHeight={true}
    />
  );
};

/**
 * Card loading spinner - for card content areas
 */
export const CardLoadingSpinner = ({ message = 'Loading content...' }) => {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600 mx-auto mb-4" />
        <p className="text-slate-600">{message}</p>
      </div>
    </div>
  );
};

export default LoadingSpinner;
